import React from 'react';
import { BuyNowGooglePayButton } from './BuyNowGooglePayButton';
// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import {
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  ListingGooglePayButtonFieldsFragment,
  Core_Checkout_StartListingGooglePayMutation,
  Core_Checkout_StartListingGooglePayMutationVariables,
} from '@reverbdotcom/commons/src/gql/graphql';
import { CheckoutStartHandler, GooglePayManagerFragment } from './googlePayManager';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { withGooglePayCheckoutResult } from './googlePayHelpers';
import { trackBuyNowOptionClick } from './buyNowTracking';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

type Listing = ListingGooglePayButtonFieldsFragment;

interface Props {
  listing: Listing;
  quantity: number;
  small?: boolean;
}

export function ListingGooglePayButton({
  listing,
  quantity,
  small = false,
}: Props) {
  const { googlePay, estimatedTotal } = listing.expressPay;
  const startCheckout = useStartCheckout(listing, quantity);
  const user = useUser();

  function trackClick() {
    trackBuyNowOptionClick({
      listingId: listing.id,
      paymentMethod: CheckoutPaymentMethodType.ANDROID_PAY,
      user,
    });
  }

  return (
    <BuyNowGooglePayButton
      estimatedTotal={estimatedTotal}
      googlePayDetails={googlePay}
      startCheckout={startCheckout}
      onClick={trackClick}
      small={small}
    />
  );
}

export function useStartCheckout(listing: Listing, quantity: number): CheckoutStartHandler {
  const [startCheckout] = useMutation<
    Core_Checkout_StartListingGooglePayMutation,
    Core_Checkout_StartListingGooglePayMutationVariables
  >(START_CHECKOUT_MUTATION);

  return React.useCallback(() => withGooglePayCheckoutResult(async () => {
    const result = await startCheckout({
      variables: {
        listingId: listing.id,
        quantity: quantity,
      },
    });
    return result.data.startCheckoutForListing.checkout;
  }), [startCheckout, listing.id, quantity]);
}

export const ListingGooglePayButtonFragment = gql`
  fragment ListingGooglePayButtonFields on Listing {
    _id
    id
    expressPay {
      _id
      estimatedTotal {
        amount
        currency
      }
      googlePay {
        merchantId
        merchantName
        gatewayMerchantId
        cardNetworks
      }
    }
  }
`;

const START_CHECKOUT_MUTATION = gql`
  mutation Core_Checkout_StartListingGooglePay($listingId: String, $quantity: Int) {
    startCheckoutForListing(
      input: {
        listingId: $listingId,
        quantity: $quantity,
        paymentMethod: ANDROID_PAY
      }
    ) {
      checkout {
        _id
        ...GooglePayManagerFields
      }
    }
  }
  ${GooglePayManagerFragment}
`;
